import React, { useState } from 'react';
import { theme } from '../theme';
import { SkeletonBlock, HANDLING_ICONS, type HandlingIconName } from './shared';
import { pillFace } from './SecondaryAxisPills';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import {
  listVocabulary, saveVocabularyLabel, resetVocabularyLabel,
  VocabularyAxisEntry, VocabularyValueEntry,
} from '../services/api';
import { formatBackendError } from '../utils/formatError';
import { useRevisionedDrafts } from '../hooks/useRevisionedDrafts';
import { suggestAbbr, ABBR_PATTERN } from '../services/abbrSuggestion';
import { btnStyle, feedbackStyle, overlayChipStyle, InlineErrorZone, staleConflictNotice } from './overlayEditorChrome';

// <purpose>The row LIST behind the vocabulary overlay rail: the axis values VokabularVerwaltung
// mounts. (Part 4b briefly generalised this over a second, `section`-parameterised list — the
// FAMILY groupings a now-deleted "Oberfläche" card mounted; operator ruling R36.6 (2026-09-28)
// deleted that card and its families section outright, so this file is back to axes only, and the
// `section` prop that told the two apart is gone — no dead parameter.) Per row this owns: the
// label draft (via the shared useRevisionedDrafts engine), the LETTERS band drawn as the value's
// own colour, a read-only served-icon box, a stated suggestion rule that prefills the letters from
// the label, and the coded refusal messages a duplicate label or duplicate letters produces.</purpose>
// <dependencies>services/api (listVocabulary/saveVocabularyLabel/resetVocabularyLabel);
// services/abbrSuggestion (suggestAbbr, ABBR_PATTERN); useRevisionedDrafts (the shared
// overlay-editing engine — label draft only, see <constraints>); overlayEditorChrome (the tab's
// one visual dialect); useCategoryMeta (the served hue for the letters/icon boxes, and `refresh()`
// — the app root's config refetch, Task 7); HANDLING_ICONS/HandlingIconName (shared.tsx — the ONE
// mapping from a served icon NAME to a drawn glyph).</dependencies>
// <constraints>The rail can NEVER add or remove a value — the catalog is driven by the tenant's
// SEED vocabulary, one row per declared value, and a save for anything else is refused by
// the backend. So this screen edits WORDINGS (and, on the PRIMARY axis, LETTERS) only.
// A reset is not a restore: it drops this tenant's admin row so whichever layer sits beneath
// re-surfaces (label AND letters together — one admin row per value). The confirmation NAMES the
// wording that takes over (revertsTo) rather than promising an unspecified "Standard".
// The LABEL draft rides the shared engine (useRevisionedDrafts) exactly as before. LETTERS do
// NOT — a value's compact spelling is not baseRevision-guarded the SAME WAY the label is (both
// freeze on their own first-dirty transition via the engine's freezeIfUnfrozen/setDraftValue pair,
// but letters ride a PARALLEL local state, abbrDrafts/suggested/abbrTouched, rather than a second
// engine instance). This is deliberate: two engines racing their own reload timing over the same
// row would be exactly the kind of two-clock bug the single label engine was built to avoid.
// SUGGESTION RULE (R34.4): editing the label prefills the letters via suggestAbbr(), UNLESS the
// admin has already touched the letters field for this row (abbrTouched) — touching the letters
// clears the mark and freezes the field against further auto-fill until the row is saved or reset.
// LETTERS ARE A PRIMARY-AXIS FACT ONLY (controller ruling, final review, fleet-wide) — the band,
// the icon box, the suggestion prefill and the `abbr` POST field all gate on the ROW's own served
// `lettersEditable` flag (carried from its axis entry by flattenAxes), never on the axis NAME: a
// non-primary axis (e.g. wim's process_type, vnb's anliegen, msb's grund_code) is
// `lettersEditable: false` on the wire and must show no letters chrome even though it IS an axis.
// An older backend that predates the field serves no flag on any axis, which floors to `false`
// everywhere — no band anywhere, never a silent `true`.
// Loading posture (standing FE rule, unchanged from VokabularVerwaltung's original engine wiring):
// skeleton shimmer in PRE-ALLOCATED space, gated on `initialLoading` never `loading` — every write
// triggers a refetch, and rows once on screen stay on screen.</constraints>

const labelInputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.colors.bgLight}`,
  fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
};

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: theme.colors.navyMid, textTransform: 'uppercase',
  letterSpacing: 0.5, marginBottom: theme.spacing.sm, paddingBottom: 4,
  borderBottom: `1px solid ${theme.colors.bgLight}`,
};

const smallCaptionStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 600, color: theme.colors.textSecondary, textTransform: 'uppercase',
  letterSpacing: 0.5,
};

// The engine keys every map by one string; a value id repeats across axes (each axis's id-space is
// independent), so the axis is part of the identity. Exported for the tests that assert per-row
// targeting, and re-exported from VokabularVerwaltung for backward callers.
export function rowKeyFor(section: string, valueId: string): string {
  return `${section}::${valueId}`;
}

// One catalog row, flattened out of its axis grouping so the engine sees a plain list.
export interface FlatRow extends VocabularyValueEntry {
  section: string;
  sectionLabel: string;
  // Carried from the axis entry: whether THIS row's letters are editable at all. Controller
  // ruling, final review — letters are a PRIMARY-axis fact fleet-wide, never gated on the axis NAME.
  lettersEditable: boolean;
}

function flattenAxes(axes: VocabularyAxisEntry[]): FlatRow[] {
  return axes.flatMap(axis =>
    axis.values.map(value => ({
      ...value, section: axis.axis, sectionLabel: axis.noun,
      lettersEditable: axis.lettersEditable === true,
    })));
}

// The coded refusal shapes core's Task 4 wire sends back on a 409, mapped to the row-facing German
// sentence. `rows` is the LOADED catalog: a conflict names the owning value when it is on screen
// (the normal case) and falls back to its raw id when it is not (a row created elsewhere since
// this list last loaded) — same posture as AbsenderVerwaltung's senderErrorMessage, which this
// mirrors. Any 409 the switch does not recognise (an explicit `stale_revision`, or a bare 409 from
// an older backend that predates the codes) is the SAME lost-update recovery as before: reload and
// rebase, never guessed at as a field message.
// `section` (final review, minor) narrows the owner lookup to the SAVING row's own axis —
// `valueId` alone is not fleet-wide unique (two different axes can each declare a value with the
// same id), so a bare `find` could name the wrong owner's word on a coincidental collision.
export function vocabularyErrorMessage(
  e: any, rows: readonly FlatRow[], draftLabel: string, section: string,
): { kind: 'stale' } | { kind: 'field'; message: string } {
  const data = e?.response?.data;
  if (e?.response?.status !== 409) {
    return { kind: 'field', message: formatBackendError(e, 'Speichern der Bezeichnung') };
  }
  const owner = rows.find(r => r.section === section && r.valueId === data?.valueId);
  const ownerFace = owner?.effectiveLabel ?? data?.valueId ?? '';
  switch (data?.error) {
    case 'abbr_conflict':
      return { kind: 'field', message: `Kürzel „${data.abbr}“ wird bereits von „${ownerFace}“ verwendet.` };
    case 'label_conflict': {
      const sameWord = ownerFace.trim().toLocaleLowerCase('de-DE') === draftLabel.trim().toLocaleLowerCase('de-DE');
      return {
        kind: 'field',
        message: `Bezeichnung „${data.label}“ wird bereits von „${ownerFace}“${sameWord ? ` (${data.valueId})` : ''} verwendet.`,
      };
    }
    default:
      return { kind: 'stale' };   // stale_revision, or a bare 409 from an older backend
  }
}

export const OverlayRows: React.FC<{
  loadErrorContext: string;
  intro: string;
}> = ({ loadErrorContext, intro }) => {
  const cm = useCategoryMeta();
  const {
    rows, drafts, loading, initialLoading, error, notice, fieldErrors,
    setNotice, load, setDraftValue, baseRevisionFor, freezeIfUnfrozen, clearFreeze, rebaseFreeze,
    setFieldError, clearFieldError, isDirty,
  } = useRevisionedDrafts<FlatRow>(
    async () => {
      const catalog = await listVocabulary();
      return flattenAxes(catalog.axes);
    },
    row => ({
      key: rowKeyFor(row.section, row.valueId),
      effectiveText: row.effectiveLabel,
      latestRevision: row.adminRevision,
    }),
    loadErrorContext,
  );

  const [savingKey, setSavingKey] = useState('');
  const [savedKey, setSavedKey] = useState('');
  const [resetConfirmKey, setResetConfirmKey] = useState<string | null>(null);
  const [resettingKey, setResettingKey] = useState('');

  // Letters live in a state PARALLEL to the engine's label draft — see <constraints>.
  const [abbrDrafts, setAbbrDrafts] = useState<Record<string, string>>({});
  const [suggested, setSuggested] = useState<Record<string, boolean>>({});
  const [abbrTouched, setAbbrTouched] = useState<Record<string, boolean>>({});

  const abbrDraftFor = (row: FlatRow) =>
    abbrDrafts[rowKeyFor(row.section, row.valueId)] ?? row.effectiveAbbr ?? '';
  const abbrDirty = (row: FlatRow) => abbrDraftFor(row) !== (row.effectiveAbbr ?? '');
  const rowDirty = (row: FlatRow) => isDirty(row) || (row.lettersEditable && abbrDirty(row));

  const clearAbbrState = (key: string) => {
    setAbbrDrafts(d => { if (!(key in d)) return d; const n = { ...d }; delete n[key]; return n; });
    setSuggested(s => { if (!(key in s)) return s; const n = { ...s }; delete n[key]; return n; });
    setAbbrTouched(t => { if (!(key in t)) return t; const n = { ...t }; delete n[key]; return n; });
  };

  const onLabelChange = (row: FlatRow, value: string) => {
    const key = rowKeyFor(row.section, row.valueId);
    // A label edit that lands back on the SERVED effective word, with the letters never having
    // been hand-touched, must drop any auto-suggested letters draft too (final review, minor) —
    // otherwise the row stays dirty (and its freeze stuck) purely because a suggestion computed
    // from an intermediate label value was never confirmed by a save (R34.4: a suggestion is
    // confirmed by SAVING it, an undone label edit confirms nothing).
    const reverting = row.lettersEditable && value === row.effectiveLabel && !abbrTouched[key];

    // The base revision freezes at the FIRST dirty transition of EITHER field and releases only
    // when BOTH are clean (fix round 1) — a label edit that goes clean while the letters are
    // still dirty must not release a freeze the letters still need. A REVERTING edit is the one
    // exception: its letters draft is about to be dropped below, so the freeze must be free to
    // release too.
    setDraftValue(row, value, () => !reverting && row.lettersEditable && abbrDirty(row));

    if (!row.lettersEditable) return;

    if (reverting) {
      clearAbbrState(key);
      return;
    }

    // A row the admin has already hand-edited the letters for is never silently overwritten by a
    // further label edit — the suggestion only fills a field the admin has not yet touched.
    if (!abbrTouched[key]) {
      setAbbrDrafts(d => ({ ...d, [key]: suggestAbbr(value) }));
      setSuggested(s => ({ ...s, [key]: true }));
    }
  };

  const onAbbrChange = (row: FlatRow, value: string) => {
    const key = rowKeyFor(row.section, row.valueId);
    clearFieldError(key);
    const upper = value.toLocaleUpperCase('de-DE');
    setAbbrDrafts(d => ({ ...d, [key]: upper }));
    setAbbrTouched(t => ({ ...t, [key]: true }));
    setSuggested(s => ({ ...s, [key]: false }));
    // The letters band dirties INDEPENDENTLY of the label engine (see <constraints>) — its own
    // clean -> dirty transition must freeze exactly like the label's does, or an edit that
    // touches only the letters sails past the optimistic-concurrency guard entirely (fix round 1:
    // a concurrent reload from another row's save could then rebase this row's base revision with
    // no user acknowledgement at all).
    const nowDirty = upper !== (row.effectiveAbbr ?? '');
    if (nowDirty) {
      freezeIfUnfrozen(row);
    } else if (!isDirty(row)) {
      // Both fields clean again — release, mirroring setDraftValue's own dirty -> clean release.
      clearFreeze(key);
    }
  };

  const discardDraft = (row: FlatRow) => {
    setDraftValue(row, row.effectiveLabel);
    if (row.lettersEditable) clearAbbrState(rowKeyFor(row.section, row.valueId));
  };

  // `load()` reseeds the LABEL draft per the engine's own rule 1 (dirty survives verbatim, clean
  // reseeds from the fresh server value); the letters draft lives outside the engine and needs the
  // identical treatment done explicitly here — a row whose letters are NOT dirty (as of the
  // PREVIOUS load, captured before this reload starts) takes the fresh effectiveAbbr, a dirty one
  // keeps its draft. Every write-triggered reload in this file goes through this wrapper instead
  // of the engine's raw `load()`, so the two drafts can never drift out of the same rule.
  const reloadWithAbbrReconcile = async (): Promise<FlatRow[]> => {
    const dirtyBefore = new Map(rows.map(r => [rowKeyFor(r.section, r.valueId), abbrDirty(r)]));
    const fresh = await load();
    setAbbrDrafts(d => {
      const next = { ...d };
      for (const row of fresh) {
        const key = rowKeyFor(row.section, row.valueId);
        if (!dirtyBefore.get(key)) next[key] = row.effectiveAbbr ?? '';
      }
      return next;
    });
    return fresh;
  };

  const save = async (row: FlatRow) => {
    const key = rowKeyFor(row.section, row.valueId);
    setSavingKey(key); setNotice(''); setSavedKey('');
    clearFieldError(key);

    // Client-side guard: a letters draft that cannot possibly be accepted never leaves this
    // machine — no request, an immediate field error instead.
    if (row.lettersEditable && abbrDraftFor(row) && !ABBR_PATTERN.test(abbrDraftFor(row))) {
      setFieldError(key, 'Kürzel: 1 bis 3 Buchstaben oder Ziffern.');
      setSavingKey('');
      return;
    }

    // `undefined` on a non-letters row (a non-primary axis) omits the key entirely —
    // there is no letters field to clear. On a letters row, a draft that matches the SEED letters
    // verbatim is sent as an explicit `null` rather than resent as an override (final review,
    // minor): typing the seed's own letters back is "no admin override", not "an override that
    // happens to equal the seed" — the latter would pin today's seed value even after a future
    // seed change moved it.
    const draftAbbr = abbrDraftFor(row);
    const abbrToSend = !row.lettersEditable
      ? undefined
      : draftAbbr === (row.seedAbbr ?? '') ? null : (draftAbbr || null);

    const baseRevision = baseRevisionFor(row);
    try {
      await saveVocabularyLabel(row.section, row.valueId, drafts[key] ?? '', baseRevision, abbrToSend);
      clearFreeze(key);
      clearAbbrState(key);
      setSavedKey(key);
      cm.refresh();
      await reloadWithAbbrReconcile();
    } catch (e: any) {
      const mapped = vocabularyErrorMessage(e, rows, drafts[key] ?? '', row.section);
      if (mapped.kind === 'stale') {
        setNotice(staleConflictNotice('Die Bezeichnung'));
        // User-acknowledged rebase — the one place the freeze moves forward without a fresh edit.
        rebaseFreeze(await reloadWithAbbrReconcile(), key);
      } else {
        setFieldError(key, mapped.message);
      }
    } finally {
      setSavingKey('');
    }
  };

  const resetToInherited = async (row: FlatRow) => {
    const key = rowKeyFor(row.section, row.valueId);
    setResettingKey(key); setNotice('');
    clearFieldError(key);
    try {
      await resetVocabularyLabel(row.section, row.valueId);
      clearFreeze(key);
      clearAbbrState(key);
      setResetConfirmKey(null);
      cm.refresh();
      await reloadWithAbbrReconcile();
    } catch (e) {
      setFieldError(key, formatBackendError(e, 'Zurücksetzen der Bezeichnung'));
    } finally {
      setResettingKey('');
    }
  };

  // Section grouping is rebuilt from the flat rows rather than kept as a second copy of the
  // catalog: one source, so a row can never render under a group it does not belong to.
  const sectionOrder: string[] = [];
  const bySection = new Map<string, FlatRow[]>();
  for (const row of rows) {
    if (!bySection.has(row.section)) { bySection.set(row.section, []); sectionOrder.push(row.section); }
    bySection.get(row.section)!.push(row);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
      <p style={{ fontSize: 12, color: theme.colors.textSecondary, margin: 0 }}>{intro}</p>

      {error && <div style={feedbackStyle(true)}>{error}</div>}
      {notice && <div style={feedbackStyle(false)}>{notice}</div>}

      {/* Refresh hint for a write-triggered refetch, in a PRE-ALLOCATED slot that is always
          present — the rows below stay mounted throughout (see initialLoading), so this line
          appearing and clearing is the only thing that changes, and its box never collapses. */}
      <div style={{ minHeight: 16 }}>
        {loading && !initialLoading && (
          <span style={{ fontSize: 11, color: theme.colors.textSecondary }}>Wird aktualisiert…</span>
        )}
      </div>

      {/* Loading posture: shimmer in pre-allocated space, FIRST load only. Three placeholder rows
          carry the same footprint a real row has (label line, input, action row, reserved error
          zone). Never placeholder TEXT, and never a stand-in for rows that are already on screen
          — see the initialLoading contract in useRevisionedDrafts. */}
      {initialLoading && (
        <div data-testid="vokabular-skeleton" aria-hidden="true"
          style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6,
              borderTop: `1px solid ${theme.colors.bgLight}`, paddingTop: theme.spacing.md }}>
              <SkeletonBlock width={180} height={15} borderRadius={6} />
              <div style={{ minHeight: 18 }} />
              <SkeletonBlock width="100%" height={35} borderRadius={6} />
              <div style={{ minHeight: 32 }} />
              <div style={{ minHeight: 48 }} />
            </div>
          ))}
        </div>
      )}

      {!initialLoading && sectionOrder.map(sectionId => {
        const sectionRows = bySection.get(sectionId)!;
        return (
          <div key={sectionId}>
            <div style={sectionHeaderStyle}>{sectionRows[0].sectionLabel}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
              {sectionRows.map(row => {
                const key = rowKeyFor(row.section, row.valueId);
                const dirty = rowDirty(row);
                const hasOwnWording = row.source === 'admin';
                const Glyph = row.lettersEditable && row.icon
                  ? HANDLING_ICONS[row.icon as HandlingIconName]
                  : undefined;
                const hue = cm.color(row.valueId);

                return (
                  <div key={key} data-testid={`vocab-row-${key}`}
                    style={{ display: 'flex', flexDirection: 'column', gap: 6,
                      borderTop: `1px solid ${theme.colors.bgLight}`, paddingTop: theme.spacing.md }}>
                    <label htmlFor={`vocab-${key}`} style={{ fontSize: 13, fontWeight: 600, color: theme.colors.navyMid }}>
                      {row.seedLabel}
                      <span style={{ fontWeight: 400, color: theme.colors.textSecondary, marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>
                        {row.valueId}
                      </span>
                    </label>

                    {/* Pre-allocated badge row — same idiom and same "only when it says something"
                        rule as the reply-prose section: no pill for a value still on its seed
                        wording (that is every value's default and conveys nothing). No
                        `flexWrap` (fix round 1, layout-stability ruling): at most two short chips
                        ever render together here, and this row's height is fixed at 18px by
                        design — a wrap would let a third chip push every row below it down. */}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', minHeight: 18 }}>
                      {hasOwnWording ? (
                        <span style={overlayChipStyle('override')}>{pillFace('info', `Angepasst · Rev ${row.adminRevision}`)}</span>
                      ) : row.source === 'institutional' ? (
                        <span style={overlayChipStyle('frozen')}>{pillFace('info', 'Vorgabe')}</span>
                      ) : null}
                      {row.lettersEditable && suggested[key] && (
                        <span style={{ ...overlayChipStyle('suggested'), animation: 'stx-fadeIn 0.3s ease' }}>{pillFace('info', 'Kürzel vorgeschlagen')}</span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'flex-end' }}>
                      <input
                        id={`vocab-${key}`}
                        type="text"
                        // Fix loop pass 4: `flex: 1` alone stretches this field to whatever room the
                        // row has — at the widened 1200px page (Einstellungen's shared main width)
                        // that is ~1072px for a single short word, the same "input stretched to a
                        // thin strip" shape the Routing directive field hit (that field capped at
                        // 600 — a short phrase; a vocabulary label is shorter still, so 480 here).
                        // Capped LOCALLY, not on labelInputStyle itself: the Kürzel input below
                        // reuses that same base style but already overrides its own width (56), so
                        // a shared cap would be redundant there and this stays scoped to the one
                        // field that actually needed it.
                        style={{ ...labelInputStyle, flex: 1, maxWidth: 480 }}
                        value={drafts[key] ?? ''}
                        onChange={e => onLabelChange(row, e.target.value)}
                      />

                      {row.lettersEditable && (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                          {Glyph && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                              <span style={smallCaptionStyle}>Symbol</span>
                              <div
                                data-testid={`vocab-icon-${key}`}
                                aria-label="Symbol (nicht änderbar)"
                                style={{
                                  width: 36, height: 35, borderRadius: 6, backgroundColor: hue,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                              >
                                <Glyph size={16} color={theme.colors.white} />
                              </div>
                            </div>
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                            <span style={smallCaptionStyle}>Kürzel</span>
                            <input
                              id={`vocab-abbr-${key}`}
                              maxLength={3}
                              value={abbrDraftFor(row)}
                              onChange={e => onAbbrChange(row, e.target.value)}
                              style={{
                                ...labelInputStyle, width: 56, textAlign: 'center', fontWeight: 700,
                                letterSpacing: 1, textTransform: 'uppercase', color: theme.colors.white,
                                backgroundColor: hue, borderColor: hue,
                              }}
                              aria-label="Kürzel"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Pre-allocated action row — buttons swap in and out inside it, the row's own
                        footprint never changes. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexWrap: 'wrap', minHeight: 32 }}>
                      <button onClick={() => save(row)} style={btnStyle('primary')}
                        disabled={savingKey === key || !dirty}>
                        {savingKey === key ? 'Speichern…' : 'Speichern'}
                      </button>

                      {dirty && (
                        <button onClick={() => discardDraft(row)} style={btnStyle('ghost')}>
                          Zurücksetzen
                        </button>
                      )}

                      {hasOwnWording && (
                        resetConfirmKey === key ? (
                          <>
                            {/* The confirmation NAMES what takes over (revertsTo), because the
                                backend already knows it — see <constraints>. */}
                            <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>
                              {`Eigene Bezeichnung verwerfen und „${row.revertsTo}“ verwenden?`}
                            </span>
                            <button onClick={() => resetToInherited(row)} style={btnStyle('danger')}
                              disabled={resettingKey === key}>
                              {resettingKey === key ? 'Setzt zurück…' : 'Bestätigen'}
                            </button>
                            <button onClick={() => setResetConfirmKey(null)} style={btnStyle('ghost')}>
                              Abbrechen
                            </button>
                          </>
                        ) : (
                          <button onClick={() => setResetConfirmKey(key)} style={btnStyle('ghost')}>
                            Eigene Bezeichnung entfernen
                          </button>
                        )
                      )}

                      {savedKey === key && !dirty && (
                        <span style={{ fontSize: 12, color: '#1f7a3d' }}>gespeichert</span>
                      )}
                    </div>

                    <InlineErrorZone rowKey={key} message={fieldErrors[key]} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {!initialLoading && rows.length === 0 && (
        <div style={{ color: theme.colors.textSecondary, fontSize: 13 }}>
          Kein Vokabular hinterlegt.
        </div>
      )}
    </div>
  );
};

export default OverlayRows;
